import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'

export interface Notification {
  id: string
  type: 'comment' | 'like' | 'admin_action' | 'weekly_update' | 'content_removed'
  title: string
  message: string
  link?: string
  read: boolean
  createdAt: string
  userId?: string
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  clearNotification: (id: string) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

// Map Supabase row to our Notification shape
const mapRow = (row: Record<string, unknown>): Notification => ({
  id: row.id as string,
  type: row.type as Notification['type'],
  title: row.title as string,
  message: row.message as string,
  link: (row.link as string) || undefined,
  read: row.read as boolean,
  createdAt: row.created_at as string,
  userId: row.user_id as string,
})

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])

  // Fetch notifications from Supabase
  const fetchNotifications = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50)

    if (error) {
      console.error('Error fetching notifications:', error)
      return
    }

    if (data) {
      setNotifications(data.map(mapRow))
    }
  }, [])

  // Load notifications and subscribe to realtime when user changes
  useEffect(() => {
    if (!user) {
      setNotifications([])
      return
    }

    fetchNotifications(user.id)

    // Subscribe to realtime INSERT events for this user's notifications
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotif = mapRow(payload.new as Record<string, unknown>)
          setNotifications(prev => [newNotif, ...prev])
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, fetchNotifications])

  const addNotification = async (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => {
    const targetUserId = notification.userId
    if (!targetUserId) return

    const { error } = await supabase
      .from('notifications')
      .insert({
        user_id: targetUserId,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        link: notification.link || null,
      })

    if (error) {
      console.error('Error creating notification:', error)
    }
    // The realtime subscription will pick up the new notification for the target user
  }

  const markAsRead = async (id: string) => {
    setNotifications(prev =>
      prev.map(notif => (notif.id === id ? { ...notif, read: true } : notif))
    )

    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id)
  }

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })))

    if (user) {
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false)
    }
  }

  const clearNotification = async (id: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id))

    await supabase
      .from('notifications')
      .delete()
      .eq('id', id)
  }

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
